import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { useAuth } from '@/contexts/AuthContext'
import { useDesign } from '@/contexts/DesignContext'
import { useNavigation } from '@/contexts/NavigationContext'
import { useToast } from '@/contexts/ToastContext'
import { ApiError } from '@/services/api/client'
import { cn } from '@/lib/cn'

/**
 * Client login — `POST /client/auth/login` with account_id + password
 * (API_DOCUMENTATION §5.2). Main password → trade:full; investor → trade:read.
 */
export function LoginPage() {
  const { t } = useTranslation('common')
  const { design } = useDesign()
  const { login } = useAuth()
  const { setRoute } = useNavigation()
  const { pushToast } = useToast()
  const [accountId, setAccountId] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!accountId.trim() || !password) return
    setLoading(true)
    setError(null)
    try {
      const session = await login({
        account_id: accountId.trim(),
        password,
      })
      setRoute('trade')
      pushToast({
        title: t('status.connected'),
        message:
          session.trade_mode === 'read'
            ? t('auth.tradeModeRead')
            : t('auth.tradeModeFull'),
        tone: 'success',
      })
      if (window.location.pathname === '/login') {
        window.history.replaceState({}, '', '/')
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? (err.fieldError('account_id') ??
            err.fieldError('password') ??
            err.message)
          : t('auth.loginFailed')
      setError(message)
      pushToast({ tone: 'danger', title: t('auth.loginFailed'), message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-terminal-bg p-4">
      <div className="absolute end-4 top-4 flex gap-2 pt-[env(safe-area-inset-top)]">
        <LanguageSwitcher />
        <ThemeSwitcher />
      </div>

      <form
        onSubmit={(e) => void onSubmit(e)}
        className={cn(
          'w-full max-w-sm rounded border border-terminal-border bg-terminal-surface p-6 shadow-2xl',
        )}
      >
        <div className="mb-6 flex flex-col items-center gap-3">
          <BrandLogo height={36} />
          <p className="text-center text-xs text-terminal-muted">
            {design.pwa.description}
          </p>
        </div>

        <label className="mb-3 flex flex-col gap-1 text-xs text-terminal-muted">
          {t('auth.accountId')}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="username"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            required
            placeholder={t('auth.accountIdPlaceholder')}
            className={cn(
              'h-9 rounded border border-terminal-border bg-terminal-bg px-2.5 text-sm text-terminal-fg',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
            )}
          />
        </label>

        <PasswordInput
          id="login-password"
          label={t('auth.password')}
          value={password}
          onChange={setPassword}
          required
          showStrength={false}
          autoComplete="current-password"
          className="mb-3"
        />

        {error ? (
          <p className="mb-3 text-[11px] leading-relaxed text-danger" role="alert">
            {error}
          </p>
        ) : (
          <p className="mb-3 text-[11px] leading-relaxed text-terminal-muted">
            {t('auth.loginHint')}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !accountId.trim() || !password}
          className={cn(
            'h-10 w-full rounded text-sm font-semibold text-white',
            'bg-accent hover:bg-accent-muted disabled:opacity-50',
          )}
        >
          {loading ? t('actions.working') : t('actions.confirm')}
        </button>
      </form>
    </div>
  )
}
